import React, { FC, useEffect, useState } from "react";

import { FormTextInput } from "stentor-models";

import TextField from "@mui/material/TextField";

import { Styleable } from "../../types";
import { formatToInputProps } from "../../utils/formatToInputProps";
import { FormFieldProps } from "../FormDisplay";

import styles from "./FormFieldTextInput.module.scss";

export interface FormTextInputProps extends FormFieldProps {
    readonly field: Styleable<FormTextInput>;
    readonly value?: string | undefined;
    /**
     * When this is the last field of the step the keyboard shows "done" rather than "next".
     */
    readonly isLastField?: boolean;
}

// Show counter when within this percentage of the limit
const COUNTER_THRESHOLD_PERCENT = 0.8;

export const FormFieldTextInput: FC<FormTextInputProps> = (props) => {
    const { field, onFieldChange, value, errorMessage, required, isLastField } = props;

    const [text, setText] = useState(value || "");

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setText(value);
    };

    useEffect(() => {
        onFieldChange(field.name, text);
    }, [field.name, onFieldChange, text]);

    useEffect(() => {
        setText(value || "");
    }, [value]);

    const maxLength = field.maxLength;
    const currentLength = text.length;
    const isAtLimit = maxLength !== undefined && currentLength >= maxLength;
    const showCounter = maxLength !== undefined && currentLength >= maxLength * COUNTER_THRESHOLD_PERCENT;

    // field.type is the widget discriminator ("TEXT" | "DROPDOWN" | ...), not an HTML
    // input type - the real semantics live on field.format.
    const { type, ...inputHelpers } = formatToInputProps({
        format: field.format,
        name: field.name,
        isLastField,
    });

    return (
        <div className={styles.xapp_text_input_container}>
            {field.title && (
                <div className={styles["xapp_text_input_container__title"]}>
                    <p>{field.title}</p>
                </div>
            )}

            <div className={styles["xapp_text_input_container__field"]}>
                <TextField
                    className={styles.xapp_text_input_text}
                    label={field.label}
                    fullWidth
                    margin="normal"
                    type={type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={text}
                    onChange={onChange}
                    variant="filled"
                    size="small"
                    error={!!errorMessage}
                    helperText={errorMessage}
                    required={required}
                    multiline={field.multiline}
                    rows={field.rows}
                    maxRows={field.rowsMax}
                    style={field.style}
                    slotProps={{ htmlInput: field.maxLength ? { ...inputHelpers, maxLength: field.maxLength } : inputHelpers }}
                />
                {showCounter && (
                    <div className={`${styles.xapp_text_input_counter} ${isAtLimit ? styles["xapp_text_input_counter--limit"] : ""}`}>
                        {currentLength}/{maxLength}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormFieldTextInput;
